import { API_BASE } from "./config";
import type {
  AnalyticsContext,
  CreateResponse,
  LeadResponse,
  Locale,
  LocationId,
  PatientForm,
  Slot,
  SlotsResponse,
} from "./types";

// DEV-only: in production a leaked ?mock=1 link would fake a booking, show a
// real "You're all set!" screen, and fire a genuine ads conversion with the
// visitor's gclid. Demo/QA use `npm run dev`.
const forceMock = import.meta.env.DEV && new URLSearchParams(window.location.search).get("mock") === "1";

async function postJSON<T>(fn: string, body: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(`${API_BASE}/${fn}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) detail = j.error;
    } catch {
      /* non-json error body */
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

async function loadSlots(location: LocationId, weeks: number): Promise<SlotsResponse> {
  if (forceMock) return mockSlots(location);
  try {
    return await postJSON<SlotsResponse>("bondWebBookingSlots", { location, weeks });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[api] slots fetch failed, using mock:", err);
      return mockSlots(location);
    }
    return { success: false, locationId: location, timezone: "America/Los_Angeles", slots: [], error: String(err) };
  }
}

// Slot loads take seconds when the backend's caches are cold (captcha mint +
// multi-week availability search), so the widget starts one as soon as an
// office is known and the time step reuses it. Failed loads self-evict so a
// transient error is retried at view time instead of pinned for the TTL.
const SLOTS_PREFETCH_TTL_MS = 120_000;
const slotsInflight = new Map<LocationId, { at: number; promise: Promise<SlotsResponse> }>();

function startSlotsLoad(location: LocationId, weeks: number): Promise<SlotsResponse> {
  const promise = loadSlots(location, weeks);
  slotsInflight.set(location, { at: Date.now(), promise });
  void promise.then((r) => {
    if (r.success === false) slotsInflight.delete(location);
  });
  return promise;
}

export function prefetchSlots(location: LocationId, weeks: number): void {
  const hit = slotsInflight.get(location);
  if (hit && Date.now() - hit.at < SLOTS_PREFETCH_TTL_MS) return;
  void startSlotsLoad(location, weeks);
}

/** Drop the cached load for an office (e.g. a booking failed on a stale slot). */
export function invalidateSlots(location: LocationId): void {
  slotsInflight.delete(location);
}

/**
 * Seed the cache with availability we already hold, so returning to the time step
 * shows it immediately instead of spinning through another round trip. Used when a
 * booking loses the race for a slot and the backend hands back current times with
 * the rejection — those are fresher than anything we could refetch.
 *
 * This writes the api-layer cache rather than component state on purpose: entering
 * the time step always clears state and refetches, which would discard an injection.
 */
export function primeSlots(location: LocationId, slots: Slot[], timezone: string): void {
  const response: SlotsResponse = { success: true, locationId: location, timezone, slots };
  slotsInflight.set(location, { at: Date.now(), promise: Promise.resolve(response) });
}

export async function fetchSlots(location: LocationId, weeks: number): Promise<SlotsResponse> {
  const hit = slotsInflight.get(location);
  if (hit && Date.now() - hit.at < SLOTS_PREFETCH_TTL_MS) return hit.promise;
  return startSlotsLoad(location, weeks);
}

export interface LeadArgs {
  location: LocationId;
  /** Present on re-submits (user went back to edit their info) — the backend
   *  updates the same lead record instead of creating a duplicate. */
  leadId?: string;
  /** Background capture before the visitor clicks Continue (name may be empty). */
  partial?: boolean;
  patient: Pick<PatientForm, "firstName" | "lastName" | "phone" | "email">;
  analytics: AnalyticsContext;
  recaptchaToken: string;
  locale: Locale;
}

/**
 * Persist the lead the moment contact info is entered, BEFORE slots are shown,
 * so an abandon at slot-selection still leaves the front desk a followable lead.
 * Best-effort: a failure here never blocks the visitor from seeing availability.
 */
export async function captureLead(args: LeadArgs): Promise<LeadResponse> {
  if (forceMock) return { success: true, leadId: `mock_lead_${Date.now()}` };
  try {
    return await postJSON<LeadResponse>("bondWebBookingLead", args);
  } catch (err) {
    if (import.meta.env.DEV) return { success: true, leadId: `mock_lead_${Date.now()}` };
    return { success: false, error: String(err) };
  }
}

export interface CreateArgs {
  location: LocationId;
  searchKey: string;
  /** Links the booking to the lead captured earlier so the same record converts. */
  leadId?: string;
  patient: PatientForm;
  analytics: AnalyticsContext;
  recaptchaToken: string;
  locale: Locale;
}

export async function createBooking(args: CreateArgs): Promise<CreateResponse> {
  if (forceMock) return mockCreate(args);
  try {
    return await postJSON<CreateResponse>("bondWebBookingCreate", args);
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[api] booking failed, using mock:", err);
      return mockCreate(args);
    }
    return { success: false, error: String(err) };
  }
}

/** Callback-request fallback when there are no online openings. */
export async function submitWaitlist(args: {
  location: LocationId;
  /** Reuse the captured lead's record so the waitlist flips its stage instead
   *  of creating a duplicate dashboard row. */
  leadId?: string;
  patient: Partial<PatientForm>;
  analytics: AnalyticsContext;
  recaptchaToken: string;
  locale: Locale;
}): Promise<CreateResponse> {
  if (forceMock) return { success: true, requestId: "mock_waitlist" };
  try {
    return await postJSON<CreateResponse>("bondWebBookingLead", { ...args, waitlist: true });
  } catch (err) {
    if (import.meta.env.DEV) return { success: true, requestId: "mock_waitlist" };
    return { success: false, error: String(err) };
  }
}

// ---- Mock backend (local UI dev only) -------------------------------------

function fmt(d: Date) {
  const tz = "America/Los_Angeles";
  return {
    date: new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: tz }).format(d),
    time: new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz }).format(d),
    datetime: new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: tz }).format(d),
  };
}

function mockSlots(location: LocationId): SlotsResponse {
  // Mirror the real Garcia inventory shape (live pull 2026-06-11: ~230-250 slots,
  // ~2 open weekdays/week with 10-20 times each from late morning to evening,
  // first opening about a week out) so demos look like production.
  const slots: Slot[] = [];
  const now = new Date();
  const openDows = location === "GARCIA_MENIFEE" ? [1, 3] : [2, 4]; // Mon/Wed vs Tue/Thu
  const utcOffset = 7; // PDT; demo data, so the small PST drift is fine
  for (let day = 6; day <= 56; day++) {
    const base = new Date(now);
    base.setDate(now.getDate() + day);
    if (!openDows.includes(base.getDay())) continue;
    // 11:20 AM - 7:00 PM PT in ~20-minute steps, with some already taken.
    for (let m = 680, i = 0; m <= 1140; m += 20, i++) {
      if ((day + i) % 4 === 0) continue;
      const d = new Date(base);
      d.setUTCHours(Math.floor(m / 60) + utcOffset, m % 60, 0, 0);
      const f = fmt(d);
      slots.push({ searchKey: `mock-${location}-${d.toISOString()}`, iso: d.toISOString(), ...f, hour: m / 60 });
    }
  }
  return { success: true, locationId: location, timezone: "America/Los_Angeles", slots };
}

function mockCreate(args: CreateArgs): CreateResponse {
  const slotIso = args.searchKey.startsWith("mock-")
    ? args.searchKey.split(/mock-[^-]+-/).pop() ?? new Date().toISOString()
    : new Date().toISOString();
  return {
    success: true,
    requestId: `mock_${Date.now()}`,
    appointmentTime: slotIso,
    appointmentDisplay: fmt(new Date(slotIso)).datetime,
  };
}
